import { CINEMATIC_ARCHETYPE } from './CinematicArchetype.js';
import { MotivicAnalysis } from './MotivicAnalysis.js';

export class ScoringEngine {

    /**
     * Evaluates the generated content against the Reference Archetype.
     * @param {Object} generatedData - Contains { events, progression, structure }
     * @param {Object} archetype - The Statistical Archetype (Defaults to CINEMATIC)
     * @returns {Object} { score: number, confidence: number, feedback: string, details: Object, gradients: Object }
     */
    static evaluate(generatedData, archetype = CINEMATIC_ARCHETYPE) {
        if (!generatedData || !generatedData.events || generatedData.events.length === 0) {
            return { score: 0, confidence: 0, feedback: "No events generated.", details: {}, gradients: {} };
        }

        const scores = {};
        const gradients = {}; // Signed Deltas (Target - Actual)
        let totalScore = 0;
        let totalWeight = 0;

        // 1. Analyze Harmonic Rhythm
        if (generatedData.progression && archetype.harmonicRhythm) {
            const hResult = this.analyzeHarmonicRhythm(generatedData.progression, generatedData.structure, archetype.harmonicRhythm);
            scores.harmonicRhythm = hResult.score;
            gradients.harmonicRhythm = hResult.gradient;

            totalScore += hResult.score * archetype.harmonicRhythm.weight;
            totalWeight += archetype.harmonicRhythm.weight;
        }

        // 2. Analyze Voice Distribution
        if (archetype.voiceDistribution) {
            const vResult = this.analyzeVoiceDistribution(generatedData.events, generatedData.structure, archetype.voiceDistribution);
            scores.voiceDistribution = vResult.score;
            gradients.voiceDistribution = vResult.gradient; // Object { low, mid, high } deltas

            totalScore += vResult.score * archetype.voiceDistribution.weight;
            totalWeight += archetype.voiceDistribution.weight;
        }

        // 3. Analyze Dynamic Curve
        if (archetype.dynamicRange) {
            const dResult = this.analyzeDynamicCurve(generatedData.events, archetype.dynamicRange);
            scores.dynamicRange = dResult.score;
            gradients.dynamicRange = dResult.gradient;

            totalScore += dResult.score * archetype.dynamicRange.weight;
            totalWeight += archetype.dynamicRange.weight;
        }

        // 4. Analyze Interval Entropy (Melodic Quality)
        if (archetype.intervalEntropy) {
            const cheatingPenalty = this.analyzeMelodicCheating(generatedData.events);
            const iResult = this.analyzeIntervalEntropy(generatedData.events, archetype.intervalEntropy);

            const iScore = Math.max(0, iResult.score - cheatingPenalty);
            scores.intervalEntropy = iScore;
            if (cheatingPenalty > 0.1) scores.cheatingPenalty = cheatingPenalty;

            gradients.intervalEntropy = iResult.gradient;

            totalScore += iScore * archetype.intervalEntropy.weight;
            totalWeight += archetype.intervalEntropy.weight;
        }

        // 5. Analyze Harmonic Consonance (Dissonance Index)
        if (archetype.harmonicConsonance) {
            const cScore = this.analyzeHarmonicConsonance(generatedData.events, archetype.harmonicConsonance);
            scores.harmonicConsonance = cScore;
            totalScore += cScore * archetype.harmonicConsonance.weight;
            totalWeight += archetype.harmonicConsonance.weight;
        }

        // 6. Analyze Rhythmic Lock (Groove Cohesion)
        if (archetype.rhythmicLock) {
            const rLock = this.analyzeRhythmicLock(generatedData.events);
            scores.rhythmicLock = rLock;
            totalScore += rLock * archetype.rhythmicLock.weight;
            totalWeight += archetype.rhythmicLock.weight;
        }

        // 7. Analyze Narrative Arc (Structure Cohesion)
        if (archetype.narrativeArc) {
            const nScore = this.analyzeNarrativeArc(generatedData.events, generatedData.structure);
            scores.narrativeArc = nScore;
            totalScore += nScore * archetype.narrativeArc.weight;
            totalWeight += archetype.narrativeArc.weight;
        }

        // 8. Analyze Motivic Integrity (Thematic Cohesion)
        if (archetype.motivicRepetition) {
            const mResult = MotivicAnalysis.analyze(generatedData.events, generatedData.structure);
            const mScore = mResult.score;
            scores.motivicRepetition = mScore;
            scores.motivicDetails = mResult.details;

            const target = archetype.motivicRepetition.target || 0.7;
            const tolerance = archetype.motivicRepetition.tolerance || 0.2;
            const dist = Math.abs(mScore - target);

            let weightedMScore = 1.0;
            if (dist > tolerance) {
                weightedMScore = Math.max(0, 1.0 - ((dist - tolerance) / tolerance));
            }
            // Simple gradient: Target - Actual
            gradients.motivicRepetition = target - mScore;

            totalScore += weightedMScore * archetype.motivicRepetition.weight;
            totalWeight += archetype.motivicRepetition.weight;
        }

        const voiceAnalysis = this.analyzeIndividualVoices(generatedData.events);
        scores.perVoice = voiceAnalysis;

        const finalScore = totalWeight > 0 ? totalScore / totalWeight : 0;
        const confidence = this.calculateConfidence(generatedData, archetype);

        return {
            score: parseFloat(finalScore.toFixed(2)),
            confidence: confidence,
            feedback: this.generateFeedback(scores, finalScore),
            details: scores,
            gradients: gradients
        };
    }

    static calculateConfidence(generatedData, archetype) {
        const eventCount = generatedData.events ? generatedData.events.length : 0;
        let sampleFactor = 1.0;
        if (eventCount < 10) sampleFactor = 0.1;
        else if (eventCount < 50) sampleFactor = eventCount / 50;

        const knownMetrics = [
            'harmonicRhythm', 'voiceDistribution', 'dynamicRange',
            'intervalEntropy', 'harmonicConsonance', 'rhythmicLock',
            'narrativeArc', 'motivicRepetition'
        ];

        let definedCount = 0;
        knownMetrics.forEach(key => {
            if (archetype[key] && archetype[key].weight > 0) definedCount++;
        });

        const coverageFactor = definedCount / knownMetrics.length;
        const confidence = (sampleFactor * 0.4) + (coverageFactor * 0.6);

        return parseFloat(confidence.toFixed(2));
    }

    static analyzeMelodicCheating(events) {
        const melodyEvents = events.filter(e => e.role === 'melody' || e.role === 'lead');
        if (melodyEvents.length < 5) return 0;

        const pitches = melodyEvents.map(e => e.d).filter(d => typeof d === 'number');
        const uniquePitches = new Set(pitches).size;
        const totalNotes = pitches.length;

        let penalty = 0;
        const varietyRatio = uniquePitches / totalNotes;
        if (varietyRatio < 0.2) {
            penalty += (0.2 - varietyRatio) * 2.0;
        }

        const minP = Math.min(...pitches);
        const maxP = Math.max(...pitches);
        const range = maxP - minP;

        if (range < 3 && totalNotes > 8) {
            penalty += 0.5;
        }

        return Math.min(1.0, penalty);
    }

    static analyzeIndividualVoices(events) {
        const voiceStats = {};
        events.forEach(ev => {
            if (!ev.instrumentId) return;
            const id = ev.instrumentId;
            if (!voiceStats[id]) {
                voiceStats[id] = {
                    count: 0,
                    minPitch: 127,
                    maxPitch: 0,
                    role: ev.role
                };
            }
            const stats = voiceStats[id];
            stats.count++;
            if (typeof ev.d === 'number') {
                stats.minPitch = Math.min(stats.minPitch, ev.d);
                stats.maxPitch = Math.max(stats.maxPitch, ev.d);
            }
        });

        const result = {};
        Object.keys(voiceStats).forEach(id => {
            const trackEvents = events.filter(e => e.instrumentId === id).sort((a,b) => a.s - b.s);
            let intervals = [];
            for(let i=0; i<trackEvents.length-1; i++) {
                const a = trackEvents[i];
                const b = trackEvents[i+1];
                if (typeof a.d === 'number' && typeof b.d === 'number') {
                    if (b.s - (a.s + a.len) < 8.0) {
                        intervals.push(Math.abs(b.d - a.d));
                    }
                }
            }
            const s = voiceStats[id];
            const avgInterval = intervals.length > 0 ? intervals.reduce((a,b)=>a+b,0) / intervals.length : 0;
            const steps = intervals.filter(i => i <= 2).length;
            const stepRatio = intervals.length > 0 ? steps / intervals.length : 1.0;

            result[id] = {
                role: s.role,
                noteCount: s.count,
                range: { min: s.minPitch === 127 ? 0 : s.minPitch, max: s.maxPitch, span: s.maxPitch - s.minPitch },
                avgInterval: parseFloat(avgInterval.toFixed(2)),
                stepRatio: parseFloat(stepRatio.toFixed(2))
            };
        });
        return result;
    }

    // --- REFACTORED ANALYZERS (Return { score, gradient }) ---

    static analyzeHarmonicRhythm(progression, structure, metric) {
        if (!progression || progression.length === 0) return { score: 0, gradient: 0 };
        if (!structure) return { score: 0.5, gradient: 0 };

        let totalBars = 0;
        structure.forEach(s => totalBars += (s.bars || 4));

        const avgBarsPerChord = totalBars / progression.length;

        const target = metric.target;

        // Gradient: Target - Actual
        // Positive = Needs Slower (Higher Bars/Chord)
        // Negative = Needs Faster (Lower Bars/Chord)
        const gradient = target - avgBarsPerChord;

        const diff = Math.abs(gradient);
        let score = 1.0;
        if (diff > metric.tolerance) {
            score = Math.max(0, 1.0 - ((diff - metric.tolerance) / metric.tolerance));
        }

        return { score, gradient };
    }

    static analyzeVoiceDistribution(events, structure, metric) {
        let low = 0, mid = 0, high = 0;
        let totalEnergy = 0;

        events.forEach(ev => {
            if (typeof ev.d !== 'number' || !ev.len) return;
            const energy = ev.len * (ev.vel || 0.7);
            totalEnergy += energy;
            if (ev.d < 54) low += energy;
            else if (ev.d > 70) high += energy;
            else mid += energy;
        });

        if (totalEnergy === 0) return { score: 0, gradient: { low: 0, mid: 0, high: 0 } };

        const avgVel = events.reduce((acc, ev) => acc + (ev.vel || 0), 0) / events.length;
        if (avgVel > metric.tuttiThreshold) {
            return { score: 1.0, gradient: { low: 0, mid: 0, high: 0 } };
        }

        const rLow = low / totalEnergy;
        const rMid = mid / totalEnergy;
        const rHigh = high / totalEnergy;

        // Gradient: Target - Actual
        const gradient = {
            low: metric.low - rLow,
            mid: metric.mid - rMid,
            high: metric.high - rHigh
        };

        const totalDist = Math.abs(gradient.low) + Math.abs(gradient.mid) + Math.abs(gradient.high);
        const score = Math.max(0, 1.0 - (totalDist / (metric.tolerance * 6)));

        return { score, gradient };
    }

    static analyzeDynamicCurve(events, metric) {
        if (events.length < 2) return { score: 0, gradient: 0 };

        const maxTime = events.reduce((max, ev) => Math.max(max, ev.s), 0);
        const buckets = new Array(Math.ceil(maxTime)).fill(0);
        const counts = new Array(Math.ceil(maxTime)).fill(0);

        events.forEach(ev => {
            const idx = Math.floor(ev.s);
            if (idx < buckets.length) {
                buckets[idx] += (ev.vel || 0.7);
                counts[idx]++;
            }
        });

        const timeSeries = buckets.map((sum, i) => counts[i] > 0 ? sum / counts[i] : 0).filter(v => v > 0);
        if (timeSeries.length < 2) return { score: 0, gradient: 0 };

        const stdDev = this.getStandardDeviation(timeSeries);
        let varianceScore = 0;
        let gradient = 0;

        if (metric.target === 'flat') {
            const threshold = metric.maxVariance || 0.1;
            if (stdDev <= threshold) {
                varianceScore = 1.0;
            } else {
                varianceScore = Math.max(0, 1.0 - ((stdDev - threshold) / threshold));
                gradient = -(stdDev - threshold); // Negative: Needs less variance
            }
        } else {
            // Target is high variance
            const targetMin = metric.minVariance || 0.15;
            if (stdDev >= targetMin) {
                varianceScore = 1.0;
            } else {
                varianceScore = Math.max(0, stdDev / targetMin);
                gradient = targetMin - stdDev; // Positive: Needs more variance
            }
        }

        // Shape Score (Simplification for Gradient purposes, we stick to variance gradient)
        let shapeScore = 1.0;
        if (metric.dynamicShape) {
             // ... existing shape logic ...
             // keeping it simple for gradient descent: mainly optimize variance.
        }

        return { score: varianceScore, gradient }; // Using variance gradient primarily
    }

    static analyzeRhythmicLock(events) {
        const bassEvents = events.filter(e => e.role === 'bass');
        // Filter out quiet ghost notes (rolls) from locking requirement
        const kickEvents = events.filter(e => (e.role === 'perc_low' || (e.instrumentId && e.instrumentId.includes('kick'))) && (e.vel || 0.7) > 0.4);

        if (bassEvents.length === 0 || kickEvents.length === 0) return 0.5;
        let locks = 0;
        kickEvents.forEach(k => {
            const hasBass = bassEvents.some(b => Math.abs(b.s - k.s) < 0.1);
            if (hasBass) locks++;
        });
        return locks / kickEvents.length;
    }

    static analyzeNarrativeArc(events, structure) {
        if (!structure) return 0.5;
        const barIntensity = {};
        events.forEach(e => {
            const bar = Math.floor(e.s / 4);
            if (!barIntensity[bar]) barIntensity[bar] = 0;
            barIntensity[bar] += (e.vel || 0.7);
        });
        const bars = Object.keys(barIntensity).map(Number).sort((a,b)=>a-b);
        if (bars.length === 0) return 0;
        const maxBar = bars[bars.length-1];
        const actualCurve = [];
        let maxInt = 0;
        for(let i=0; i<=maxBar; i++) {
            const val = barIntensity[i] || 0;
            actualCurve.push(val);
            if(val > maxInt) maxInt = val;
        }
        const normalizedActual = actualCurve.map(v => maxInt > 0 ? v/maxInt : 0);
        const idealCurve = [];
        structure.forEach(s => {
            const len = s.bars || 4;
            const type = s.type.toUpperCase();
            let target = 0.5;
            if (type.includes('INTRO')) target = 0.3;
            else if (type.includes('BUILD') || type.includes('RISE')) target = 0.7;
            else if (type.includes('CLIMAX') || type.includes('DROP') || type.includes('CHORUS')) target = 1.0;
            else if (type.includes('OUTRO')) target = 0.4;
            else if (type.includes('VERSE')) target = 0.6;
            for(let i=0; i<len; i++) {
                if (type.includes('BUILD')) idealCurve.push(0.4 + (0.6 * (i/len)));
                else idealCurve.push(target);
            }
        });
        let errorSum = 0;
        let points = Math.min(normalizedActual.length, idealCurve.length);
        if (points === 0) return 0;
        for(let i=0; i<points; i++) {
            const diff = normalizedActual[i] - idealCurve[i];
            errorSum += (diff * diff);
        }
        const mse = errorSum / points;
        return Math.max(0, 1.0 - Math.sqrt(mse));
    }

    static analyzeIntervalEntropy(events, metric) {
        const melodyEvents = events.filter(ev => ev.role === 'melody' || ev.role === 'lead');
        if (melodyEvents.length < 2) return { score: 1.0, gradient: 0 };

        melodyEvents.sort((a, b) => a.s - b.s);
        let steps = 0;
        let intervals = 0;
        const stepLimit = metric.stepMax || 2;

        for (let i = 0; i < melodyEvents.length - 1; i++) {
            const a = melodyEvents[i];
            const b = melodyEvents[i+1];
            if (b.s - (a.s + a.len) < 2.0) {
                const interval = Math.abs(b.d - a.d);
                if (interval <= stepLimit) steps++;
                intervals++;
            }
        }

        if (intervals === 0) return { score: 1.0, gradient: 0 };

        const ratio = steps / intervals;
        const target = metric.stepRatio;

        // Gradient: Target - Actual
        // Positive: Need Higher Ratio (More Steps) -> Reduce Range
        // Negative: Need Lower Ratio (More Leaps) -> Increase Range
        const gradient = target - ratio;

        const diff = Math.abs(gradient);
        let score = 1.0;
        if (diff > metric.tolerance) {
            score = Math.max(0, 1.0 - ((diff - metric.tolerance) / metric.tolerance));
        }

        return { score, gradient };
    }

    static analyzeHarmonicConsonance(events, metric) {
        if (events.length < 2) return 0.5;
        const maxTime = events.reduce((max, ev) => Math.max(max, ev.s + (ev.len || 0.1)), 0);
        const timeStep = 0.25;
        let totalDissonance = 0;
        let samples = 0;
        for (let t = 0; t < maxTime; t += timeStep) {
            const tEnd = t + timeStep;
            const activeNotes = events.filter(ev => {
                const s = Number(ev.s);
                const l = Number(ev.len || 0.1);
                const e = s + l;
                return (s < tEnd && e > t && typeof ev.d === 'number');
            });
            if (activeNotes.length < 2) continue;
            let intervalSum = 0;
            let intervalCount = 0;
            for (let i = 0; i < activeNotes.length; i++) {
                for (let j = i + 1; j < activeNotes.length; j++) {
                    const p1 = activeNotes[i].d % 12;
                    const p2 = activeNotes[j].d % 12;
                    let interval = Math.abs(p1 - p2);
                    if (interval > 6) interval = 12 - interval;
                    const weights = { 0: 0.0, 1: 1.0, 2: 0.8, 3: 0.2, 4: 0.1, 5: 0.3, 6: 1.0 };
                    if (interval !== 0) {
                        const w = weights[interval] || 0;
                        intervalSum += w;
                        intervalCount++;
                    }
                }
            }
            if (intervalCount > 0) {
                totalDissonance += (intervalSum / intervalCount);
                samples++;
            }
        }
        if (samples === 0) return 0.5;
        const avgDissonance = totalDissonance / samples;
        if (metric.polarity === 'dissonant') {
            return Math.min(1.0, avgDissonance * 3.0);
        } else {
            return Math.max(0, 1.0 - avgDissonance);
        }
    }

    static getStandardDeviation(array) {
        const n = array.length;
        if (n === 0) return 0;
        const mean = array.reduce((a, b) => a + b, 0) / n;
        return Math.sqrt(array.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b, 0) / n);
    }

    static generateFeedback(scores, finalScore) {
        if (finalScore > 0.8) return "Excellent Alignment with Archetype.";
        const weaknesses = [];
        if (scores.harmonicRhythm < 0.6) weaknesses.push("Harmonic Rhythm");
        if (scores.voiceDistribution < 0.6) weaknesses.push("Voice Distribution");
        if (scores.dynamicRange < 0.6) weaknesses.push("Dynamic Range");
        if (scores.intervalEntropy < 0.6) weaknesses.push("Melodic Flow");
        if (weaknesses.length === 0) return "Good Alignment.";
        return `Optimization Needed: ${weaknesses.join(', ')}`;
    }
}
